<script setup lang="ts">
// A status label: tone = colour + glyph shape, so it survives colour blindness,
// a washed-out projector and a monochrome screenshot in a bug report.
import { computed } from 'vue'
import type { Tone } from './tone'

const props = withDefaults(
  defineProps<{
    tone: Tone
    /** Text of the chip; the default slot overrides it. */
    label?: string
    /** Solid fill: for the few states that must dominate the screen. */
    solid?: boolean
    /** Blinking: an alarm nobody acknowledged yet. Ignored for calm tones. */
    blink?: boolean
    size?: 'sm' | 'md'
  }>(),
  { label: '', solid: false, blink: false, size: 'md' },
)

/** Blinking is reserved for states that call for action. */
const blinking = computed(() => props.blink && (props.tone === 'alarm' || props.tone === 'warn'))
</script>

<template>
  <span
    class="ag-chip"
    :class="[`ag-chip--${tone}`, `ag-chip--${size}`, { 'ag-chip--solid': solid, 'ag-chip--blink': blinking }]"
    :data-tone="tone"
  >
    <svg class="ag-chip__glyph" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <circle v-if="tone === 'ok'" cx="5" cy="5" r="4" />
      <path v-else-if="tone === 'warn'" d="M5 0.8 9.6 9.2H0.4Z" />
      <rect v-else-if="tone === 'alarm'" x="1" y="1" width="8" height="8" />
      <path v-else-if="tone === 'air'" d="M5 0.3 9.7 5 5 9.7 0.3 5Z" />
      <circle v-else-if="tone === 'info'" cx="5" cy="5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.6" />
      <rect v-else x="1" y="4" width="8" height="2" />
    </svg>
    <span class="ag-chip__label"><slot>{{ label }}</slot></span>
  </span>
</template>

<style scoped>
.ag-chip {
  --chip-fg: var(--ag-idle);
  --chip-bg: var(--ag-idle-bg);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 0 var(--ag-space-2);
  border: 1px solid color-mix(in srgb, var(--chip-fg) 45%, transparent);
  border-radius: var(--ag-radius-sm);
  background: var(--chip-bg);
  color: var(--chip-fg);
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
}

.ag-chip--md {
  height: 22px;
  font-size: var(--ag-font-size-sm);
}

/* 12 px, like .ag-tag: the 11 px small chip ("Работает", "поднят",
 * "не наблюдается") was the hardest text on the console to read (UIX audit). */
.ag-chip--sm {
  height: 20px;
  padding: 0 6px;
  gap: 4px;
  font-size: var(--ag-font-size-sm);
}

.ag-chip__glyph {
  flex: none;
  width: 9px;
  height: 9px;
  fill: currentColor;
}

.ag-chip__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.ag-chip--ok {
  --chip-fg: var(--ag-ok);
  --chip-bg: var(--ag-ok-bg);
}

.ag-chip--warn {
  --chip-fg: var(--ag-warn);
  --chip-bg: var(--ag-warn-bg);
}

.ag-chip--alarm {
  --chip-fg: var(--ag-alarm);
  --chip-bg: var(--ag-alarm-bg);
}

.ag-chip--air {
  --chip-fg: var(--ag-air);
  --chip-bg: var(--ag-air-bg);
}

.ag-chip--info {
  --chip-fg: var(--ag-info);
  --chip-bg: var(--ag-info-bg);
}

.ag-chip--solid {
  border-color: var(--chip-fg);
  background: var(--chip-fg);
  color: var(--ag-text-on-status);
}

.ag-chip--blink {
  animation: ag-chip-blink var(--ag-blink-period) steps(1, end) infinite;
}

@keyframes ag-chip-blink {
  50% {
    border-color: var(--chip-fg);
    background: var(--chip-fg);
    color: var(--ag-text-on-status);
  }
}

/* No motion: an unacknowledged state is shown as a heavier outline instead. */
@media (prefers-reduced-motion: reduce) {
  .ag-chip--blink {
    animation: none;
    outline: 2px solid var(--chip-fg);
    outline-offset: 1px;
  }
}
</style>
